import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    BarGradient,
    Box,
    Button,
    CalendarMonthIcon,
    CropFreeIcon,
    DispersedSourceIcon,
    Divider,
    Grid,
    LocationOnIcon,
    PlumeIcon,
    ProgressBar,
    Skeleton,
    TimeSelector,
    Typography,
    ZoomIn,
    buildLinearGradient,
    useMediaQuery,
    useTheme
} from '@methanesat/ui-components';
import { centroid, polygon } from '@turf/turf';
import { Polygon } from 'geojson';
import {
    FAQ_ALGORITHM_VERSION,
    getGlobalDateOptions,
    getTargetEmissionsRange,
    getTargetTotalsColors,
    getTimeSelectorTimespan,
    METHANESAT_FAQ_URL,
    ZOOM_LEVEL_ZOOM_FOR_DETAILS
} from '../../../../../consts';
import { SHOW_COMPOSITION_OF_EMISSIONS, SHOW_ZOOM_FOR_DETAILS } from '../../../../../environmentVariables';
import { useAreaEmissionsUnitsLabel, useGetHistoricalSamplesForTarget, useTranslate } from '../../../../../hooks';
import {
    selectAreaEmissionsWithinZoom,
    selectPlatform,
    setEmissionsMapViewState,
    useGetSingleTargetDataQuery
} from '../../../../../reducers';
import {
    AreaEmissionsProducts,
    CaptureFeatureProperties,
    MethaneEmissionsProducts,
    MethaneLayerIds,
    MSatPickInfo,
    PageIds,
    PickInfoToPrioritize,
    Platforms,
    TargetData,
    StacDateBlock,
    ViewStatePropsQueryString
} from '../../../../../types';
import {
    analytics,
    calculateDispersedIconColors,
    formatDateForUrl,
    getColorRangeColors,
    getLinearPositionOnScale,
    getStacCaptureEmissions
} from '../../../../../utils';
import {
    formatEmissions,
    formatPercentage,
    formatTotalEmissionsMinified,
    isNumber,
    roundUsingSigFigs
} from '../../../../../utils/numbers';
import { formatMeasurementTime } from '../../../../../utils/time';
import { DrawerCard } from '../../DrawerWithCards/DrawerCard';
import { EmissionTitle } from '../../DrawerWithCards/EmissionTitle';
import { DrawerCardContentHeader } from '../../Headers/DrawerCardContentHeader';
import { DataRowsModalBody, FeatureIDCaption } from '../../Layout';
import { EmissionsHeadline } from './EmissionsHeadline';
import { handleTimeSelectorDayClick, stacDatesToTimeSelector } from './helpers';
import { LinkToMain } from '../../DrawerWithCards/LinkToMain';

export interface TargetCardProps {
    info: MSatPickInfo<Polygon, CaptureFeatureProperties>;
    active: boolean;
    /** passed in from index.page.tsx - handles all top-level updates to selected info */
    handleInfoOnDateChange: (data: PickInfoToPrioritize[]) => void;
    page?: PageIds;
}

const useGetDrawerHeaders = (
    properties: CaptureFeatureProperties,
    fetchingData: boolean,
    targetData: TargetData | undefined,
    page?: PageIds
) => {
    const t = useTranslate();
    const platform = useSelector(selectPlatform);
    const { target_id: targetId, target_group_id: targetGroupId }: Partial<CaptureFeatureProperties> = properties;

    const title = t(`emissionsMapPage.mapModals.target.targetIdLabel`);

    const { basin, country } = targetData || {};
    const locationSubtitle = !fetchingData ? (
        [basin, country].filter((item) => !!item).join(', ')
    ) : (
        <Skeleton variant="text" sx={{ fontSize: '1.2rem' }} />
    );

    return {
        title,
        subtitle: page === PageIds.emissionsMap ? targetGroupId || targetId : targetId,
        ...(platform === Platforms.MSAT ? { locationSubtitle } : {})
    };
};

export const TargetCard = ({ info, active, handleInfoOnDateChange, page }: TargetCardProps) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const t = useTranslate();
    // mapping properties to new variable names
    const {
        location,
        basin,
        size_km2: sizeKm2,
        start_datetime: timeStart,
        end_datetime: timeEnd,
        ps_count: pointSourceCount,
        ps_total: pointSourceTotalKgHr,
        target_id: targetId,
        target_group_id: targetGroupId
    }: Partial<CaptureFeatureProperties> = info.object.properties;

    const id = info.object.id;

    // analytics
    useEffect(() => {
        analytics.openTargetDetails({
            itemId: id,
            date: timeStart ? formatDateForUrl(new Date(timeStart)) : '',
            targetId
        });
    }, [id, timeStart, targetId]);

    const { totalKgHr } = getStacCaptureEmissions(info.object.properties);

    const layerId = MethaneLayerIds.targets;

    const isMobileScreen = useMediaQuery(theme.breakpoints.down('md'));

    const areaEmissionLayerWithinZoom = useSelector(selectAreaEmissionsWithinZoom);

    const coordinates = info.object.geometry.coordinates;

    const zoomForDetails = () => {
        const featurePolygon = polygon(coordinates);
        const [lng, lat] = centroid(featurePolygon).geometry.coordinates;
        // magic number, slight offset in lng so that on smaller screens the target isn't behind the drawer
        const centerOffset = isMobileScreen ? 0 : 1.5;
        const recenteredViewState: ViewStatePropsQueryString = {
            longitude: lng + centerOffset,
            latitude: lat,
            zoom: ZOOM_LEVEL_ZOOM_FOR_DETAILS
        };
        dispatch(setEmissionsMapViewState(recenteredViewState as ViewStatePropsQueryString));
    };

    const selectorDates = useGetHistoricalSamplesForTarget(targetGroupId || targetId);
    const shouldShowTotalEmissions = isNumber(totalKgHr);
    const platform = useSelector(selectPlatform);

    // Although it doesn't break anything, there is no data for MAIR in the targets DB so we want to skip this API call
    const { data: targetData, isFetching: fetchingData } = useGetSingleTargetDataQuery(
        { targetId },
        { skip: platform === Platforms.MAIR }
    );

    // TODO: figure out properties
    const basinValue = basin || location;

    const { title, subtitle, locationSubtitle } = useGetDrawerHeaders(
        info.object.properties,
        fetchingData,
        targetData,
        page
    );

    const { MIN, MAX } = getTargetEmissionsRange(platform);

    const trackDayClick = useCallback(
        () => (day: StacDateBlock, timestamp: string) => {
            analytics.clickTargetDrawerObservation({
                itemId: day.id,
                platform,
                emissionRate: day.totalKgHr ?? NaN,
                label: timestamp
            });
        },
        [platform]
    );

    const dataForTimeSelector = useMemo(() => {
        /**
         * This conditional prevents passing selectorDates = `null`
         * to `stacDatesToTimeSelector` and triggering an error.
         */
        if (selectorDates)
            return stacDatesToTimeSelector({
                dates: selectorDates,
                handleClick: (day: StacDateBlock) => {
                    handleInfoOnDateChange([
                        {
                            layer: {
                                id: MethaneLayerIds.targets
                            } as PickInfoToPrioritize['layer'],
                            object: day.feature
                        } as PickInfoToPrioritize
                    ]);
                    handleTimeSelectorDayClick({
                        day,
                        dispatch,
                        targetId,
                        targetGroupId,
                        layerId,
                        trackDayClick
                    });
                }
            });
        return [];
    }, [selectorDates, dispatch, layerId, targetId, handleInfoOnDateChange, trackDayClick, targetGroupId]);

    const detailDataRows = [
        {
            label: t('emissionsMapPage.mapModals.target.dataTable.measurementTaken'),
            value: formatMeasurementTime(timeStart, timeEnd),
            mobileLabel: t('emissionsMapPage.mapModals.target.dataTable.measurementTakenMobile'),
            icon: <CalendarMonthIcon />
        },
        {
            label: t('emissionsMapPage.mapModals.common.algorithmVersionLabel'),
            href: `${METHANESAT_FAQ_URL}${FAQ_ALGORITHM_VERSION}`,
            value: info.object.properties.processor_version
        },
        ...(isNumber(sizeKm2)
            ? [
                  {
                      label: t('emissionsMapPage.mapModals.target.dataTable.sizeLabel'),
                      value: roundUsingSigFigs(sizeKm2, 'km²'),
                      icon: <CropFreeIcon />
                  }
              ]
            : []),
        {
            label: t('emissionsMapPage.mapModals.common.dataTable.locationLabel'),
            value: basinValue,
            icon: <LocationOnIcon />
        }
    ];

    const targetTotalsColors = getTargetTotalsColors(theme, true);
    const pointEmissionsIconColors = getColorRangeColors(theme, true, AreaEmissionsProducts.l4);

    const dispersedEmissionsValueLabel = totalKgHr
        ? t('emissionsMapPage.mapModals.target.emissionsPerHour', {
              kgHr: formatEmissions(totalKgHr - (pointSourceTotalKgHr || 0))
          })
        : t('emissionsMapPage.mapModals.target.pendingData.general');

    const descriptionText = t(`emissionsMapPage.mapModals.target.description.${platform}`);

    const featureIDCaption = id;

    const gradientString = buildLinearGradient(targetTotalsColors);
    const units = useAreaEmissionsUnitsLabel(MethaneEmissionsProducts.target);

    return (
        <DrawerCard data-active={active} elevation={active ? 0 : 8}>
            <Grid container flexDirection="column">
                <Grid size="grow">
                    <DrawerCardContentHeader
                        titleLabel={title}
                        titleValue={subtitle}
                        locationSubtitle={locationSubtitle}
                    />
                </Grid>
                <Grid size="grow" marginTop={3.5}>
                    <EmissionTitle
                        data-testid={`target-title-${id}`}
                        emissionTypeLabel={t('emissionsMapPage.mapModals.target.totalEmissionsTitle')}
                        emissionsHeadline={
                            <EmissionsHeadline
                                emissions={totalKgHr}
                                units={units}
                                typographyVariant="h6"
                                data-testid={`target-card-headline-${id}`}
                                {...(!totalKgHr && {
                                    emissionsText: t('emissionsMapPage.mapModals.target.pendingData.total')
                                })}
                            />
                        }
                    />
                </Grid>
                <Box paddingX={2} paddingBottom={2} paddingTop={1}>
                    <Box marginBottom={2}>
                        {shouldShowTotalEmissions ? (
                            <BarGradient
                                gradientString={gradientString}
                                min={MIN}
                                minLabel={formatTotalEmissionsMinified(MIN)}
                                max={MAX}
                                maxLabel={formatTotalEmissionsMinified(MAX)}
                                value={totalKgHr}
                                showValueLabel={false}
                                valuePosition={getLinearPositionOnScale({ min: MIN, max: MAX, value: totalKgHr })}
                            />
                        ) : (
                            <BarGradient gradientString={buildLinearGradient()} />
                        )}
                    </Box>
                    <Box marginBottom={1}>
                        <DataRowsModalBody dataRows={detailDataRows} />
                    </Box>

                    {SHOW_ZOOM_FOR_DETAILS && (
                        <Grid size={{ xs: 12 }} sx={{ textAlign: 'center' }}>
                            <Button
                                variant="outlined"
                                data-testid="zoom-details"
                                disabled={areaEmissionLayerWithinZoom}
                                onClick={() => {
                                    zoomForDetails();
                                }}
                                sx={(theme) => ({ boxShadow: theme.shadows[2] })}
                            >
                                <ZoomIn sx={{ marginRight: 1 }} />
                                {t('emissionsMapPage.mapModals.target.detailsButtonLabel')}
                            </Button>
                        </Grid>
                    )}

                    {descriptionText && (
                        <Grid container paddingTop={2}>
                            <Grid size={{ xs: 12 }}>
                                <Typography variant="body1">{descriptionText}</Typography>
                            </Grid>
                        </Grid>
                    )}

                    {/* Target emissions composition */}
                    {SHOW_COMPOSITION_OF_EMISSIONS && totalKgHr !== undefined && pointSourceTotalKgHr !== undefined && (
                        <Grid size={{ xs: 12 }}>
                            <Grid container rowGap={2}>
                                {/* Title */}
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="h4">
                                        {t('emissionsMapPage.mapModals.target.emissionComposition.title')}
                                    </Typography>
                                </Grid>
                                {/* Icons & percents */}
                                <Grid size={{ xs: 12 }}>
                                    <Grid container rowGap={1}>
                                        <Grid size={{ xs: 12 }}>
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'flex-end'
                                                }}
                                            >
                                                {/* Dispersed sources icon & percent */}
                                                <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                                    <DispersedSourceIcon
                                                        colors={calculateDispersedIconColors(
                                                            theme,
                                                            true,
                                                            AreaEmissionsProducts.l4,
                                                            platform
                                                        )}
                                                    />
                                                    {totalKgHr && (
                                                        <Typography variant="body2">
                                                            {formatPercentage(
                                                                totalKgHr - (pointSourceTotalKgHr || 0),
                                                                totalKgHr
                                                            )}
                                                            %
                                                        </Typography>
                                                    )}
                                                </Box>
                                                {/* Distinct sources icon & percent */}
                                                <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                                    {totalKgHr && (
                                                        <Typography variant="body2">
                                                            {formatPercentage(pointSourceTotalKgHr || 0, totalKgHr)}%
                                                        </Typography>
                                                    )}
                                                    <PlumeIcon sx={{ height: 24 }} colors={pointEmissionsIconColors} />
                                                </Box>
                                            </Box>
                                        </Grid>
                                        <Grid size={{ xs: 12 }}>
                                            {/* 
                              progress bar: if we are missing the total, display an 'empty'/0% progress bar
                            */}
                                            {totalKgHr ? (
                                                <ProgressBar
                                                    percent={formatPercentage(
                                                        totalKgHr - (pointSourceTotalKgHr || 0),
                                                        totalKgHr
                                                    )}
                                                />
                                            ) : (
                                                <ProgressBar percent={0} />
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                                {/* data table for progress bar */}
                                <Grid
                                    size={{ xs: 12 }}
                                    sx={{ '& .MuiTypography-root:not(:last-child)': { marginBottom: 0.5 } }}
                                >
                                    <Grid container flexDirection="row" justifyContent="space-between">
                                        <Grid
                                            size="grow"
                                            textAlign="start"
                                            display="flex"
                                            justifyContent="space-between"
                                            flexDirection="column"
                                        >
                                            <Typography variant="body1" fontWeight={700} lineHeight="1">
                                                {t(
                                                    `emissionsMapPage.mapModals.target.emissionComposition.areaEmissionsLabel`
                                                )}
                                            </Typography>
                                            <Typography variant="body2">{dispersedEmissionsValueLabel}</Typography>
                                        </Grid>
                                        <Grid
                                            size="grow"
                                            textAlign="end"
                                            display="flex"
                                            justifyContent="space-between"
                                            flexDirection="column"
                                        >
                                            <Typography variant="body1" fontWeight={700} lineHeight="1">
                                                {t(
                                                    `emissionsMapPage.mapModals.target.emissionComposition.pointSourcesLabel.${pointSourceCount === 1 ? 'singular' : 'plural'}`,
                                                    {
                                                        pointSourceCount: pointSourceCount
                                                    }
                                                )}
                                            </Typography>
                                            <Typography variant="body2">
                                                {t('emissionsMapPage.mapModals.target.emissionsPerHour', {
                                                    kgHr: formatEmissions(pointSourceTotalKgHr || 0)
                                                })}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                </Box>

                {/** Historical Time Selector */}
                {page !== PageIds.singleCollection && (
                    <>
                        <Divider />
                        <Grid size="grow">
                            <Grid container marginTop={3} marginBottom={3} rowGap={2}>
                                <Grid size={{ xs: 12 }}>
                                    <Typography fontWeight="bold">
                                        {t('emissionsMapPage.mapModals.common.overTimeTitle')}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <TimeSelector
                                        data-testid="time-selector-day-picker"
                                        data={dataForTimeSelector}
                                        globalDateOptions={getGlobalDateOptions(platform)}
                                        enableResizeObserver
                                        variant={getTimeSelectorTimespan(platform)}
                                        selectedValue={timeStart}
                                        boxHeight="36px"
                                        layout="horizontal"
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </>
                )}

                {page === PageIds.singleCollection && (
                    <Grid size="grow" sx={{ marginBottom: 2 }}>
                        <LinkToMain targetId={targetId} startDateTime={timeStart} />
                    </Grid>
                )}
                {/* footer content - link & collection id */}
                <Grid size={{ xs: 12 }}>
                    <Grid container rowGap={2}>
                        {/* collection id for debugging */}
                        <Grid size={{ xs: 12 }}>
                            <FeatureIDCaption caption={`${featureIDCaption}-${targetId}`} />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </DrawerCard>
    );
};
